import { ExternalLink, Loader2, RefreshCw, Save } from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { AdminState } from "../components/admin/AdminState";
import { PageHeader } from "../components/admin/PageHeader";
import { StatusBadge } from "../components/admin/StatusBadge";
import { adminPost, type SettingsField, type SettingsGroup, useAdminQuery } from "../components/admin/adminApi";

type WidgetsResponse = {
  group: SettingsGroup;
  ok: true;
  preview: {
    averageRating: number;
    ctaText: string;
    productTitle: string;
    quote: string;
    reviewCount: number;
    reviewer: string;
  };
  themeSetup: {
    activateEmbed: string;
    addMediaGallery: string;
    addProductReviews: string;
    addRatingBadge: string;
    editor: string;
  };
  widgets: Array<{ enabled: boolean; name: string; status: string }>;
};

type SettingValue = boolean | number | string;
type WidgetOverrides = Record<string, Record<string, SettingValue>>;
type WidgetDraft = Record<string, SettingValue | WidgetOverrides | undefined>;

const widgetCatalog = [
  {
    description: "A full product review section with filters, sorting, media, replies, and verified-buyer badges.",
    key: "reviews",
    surface: "product-reviews",
    title: "Reviews Widget",
    visual: "reviews",
  },
  {
    description: "A compact rating badge for product titles and collection cards with review count and SEO metadata.",
    key: "rating",
    surface: "rating-badge",
    title: "Star Rating Widget",
    visual: "rating",
  },
  {
    description: "A timed social-proof card that shuffles approved reviews and links shoppers back to the product.",
    key: "popup",
    surface: "popup",
    title: "Pop-up Widget",
    visual: "popup",
  },
  {
    description: "A swipeable carousel for featured customer photos, videos, and strongest written reviews.",
    key: "carousel",
    surface: "review-carousel",
    title: "Dynamic Carousel",
    visual: "carousel",
  },
  {
    description: "A persistent sidebar entry point that opens verified reviews without interrupting browsing.",
    key: "sidebar",
    surface: "floating-tab",
    title: "Reviews Sidebar",
    visual: "sidebar",
  },
  {
    description: "A compact proof badge for landing pages, collection pages, and trust sections.",
    key: "trust",
    surface: "trust-badge",
    title: "Trust Badge",
    visual: "trust",
  },
  {
    description: "Short quote cards that surface persuasive review snippets across product and home pages.",
    key: "snippets",
    surface: "review-snippets",
    title: "Snippets Widget",
    visual: "snippets",
  },
  {
    description: "A customer-media gallery for photo and video proof, tuned for mobile loading performance.",
    key: "gallery",
    surface: "media-gallery",
    title: "Media Gallery",
    visual: "gallery",
  },
];

export function WidgetsPage({ focus }: { focus?: string }) {
  const widgets = useAdminQuery<WidgetsResponse>("/api/admin/widgets");
  const [draft, setDraft] = useState<WidgetDraft>({});
  const [surface, setSurface] = useState(widgetCatalog[0].surface);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "critical" | "success"; text: string }>();
  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(widgets.data?.group.values ?? {}),
    [draft, widgets.data?.group.values],
  );

  useEffect(() => {
    if (widgets.data?.group.values) setDraft(widgets.data.group.values as WidgetDraft);
  }, [widgets.data?.group.values]);

  async function saveWidgets() {
    setBusy(true);
    setNotice(undefined);
    try {
      const response = await adminPost<WidgetsResponse>("/api/admin/widgets/settings", { values: draft });
      setDraft(response.group.values);
      setNotice({ tone: "success", text: "Widget defaults saved." });
      await widgets.reload();
    } catch (error) {
      setNotice({ tone: "critical", text: error instanceof Error ? error.message : "Widget settings could not be saved." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="wr-page">
      <PageHeader
        eyebrow={focus === "theme" ? "Theme installer" : "Storefront proof studio"}
        title={focus === "theme" ? "Widget installer" : "Storefront widgets"}
        description="Configure theme extension defaults, preview live published proof, and keep storefront widgets original."
        meta={
          <>
            <span>Async storefront loading</span>
            <span>Theme editor blocks</span>
            <span>Mobile-first widgets</span>
          </>
        }
        action={
          <div className="wr-header-actions">
            <button className="wr-button wr-button--secondary" type="button" onClick={() => void widgets.reload()}>
              <RefreshCw size={15} aria-hidden />
              Refresh
            </button>
            <a className="wr-button" href="/admin/theme-setup">
              <ExternalLink size={15} aria-hidden />
              Theme setup
            </a>
          </div>
        }
      />
      {notice ? <div className={`wr-notice wr-notice--${notice.tone} wr-toast`}>{notice.text}</div> : null}
      <AdminState
        status={widgets.status}
        error={widgets.error}
        onRetry={() => void widgets.reload()}
        emptyText="Published review widgets appear after products and settings are available."
      >
        <section className="wr-widget-catalog" aria-label="Storefront widget catalog">
          <div className="wr-section-title-row">
            <div>
              <h2>Widgets</h2>
              <p>Choose a storefront proof surface, customize it, then add it from the Shopify theme editor.</p>
            </div>
            <a className="wr-button wr-button--secondary" href={widgets.data?.themeSetup.editor ?? "/admin/theme-setup"} target="_top" rel="noreferrer">
              Open theme editor
              <ExternalLink size={15} aria-hidden />
            </a>
          </div>
          <div className="wr-widget-catalog-grid">
            {widgetCatalog.map((item) => {
              const status = widgetStatus(widgets.data?.widgets, item.title);
              return (
                <article key={item.key} className={`wr-catalog-card wr-catalog-card--${item.visual}`}>
                  <div className="wr-catalog-card__visual" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <div className="wr-catalog-card__actions">
                    <a className="wr-button wr-button--secondary" href="#whatstar-widget-overrides" onClick={() => setSurface(item.surface)}>
                      Customize
                    </a>
                    <a className="wr-button" href={widgets.data?.themeSetup.editor ?? "/admin/theme-setup"} target="_top" rel="noreferrer">
                      Add to theme
                      <ExternalLink size={14} aria-hidden />
                    </a>
                    {status ? <StatusBadge tone={status.enabled ? "success" : "warning"}>{status.status}</StatusBadge> : null}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="wr-widget-layout" id="whatstar-widget-defaults">
          <div className="wr-widget-stack">
            {focus === "theme" ? <ThemeSetupPanel links={widgets.data?.themeSetup} /> : null}
            <div className="wr-panel wr-settings-intro wr-studio-intro">
              <div>
                <h2>Widget defaults</h2>
                <p>{widgets.data?.group.description}</p>
                <div className="wr-settings-status">
                  <span className={isDirty ? "wr-settings-status__dirty" : "wr-settings-status__saved"}>
                    {isDirty ? "Unsaved changes" : "Saved"}
                  </span>
                  {widgets.data?.group.updatedAt ? <small>Last saved {formatDate(widgets.data.group.updatedAt)}</small> : null}
                </div>
              </div>
              <button className="wr-button" type="button" disabled={busy || !isDirty} onClick={() => void saveWidgets()}>
                {busy ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <Save size={15} aria-hidden />}
                Save widgets
              </button>
            </div>
            <div className="wr-widget-form-grid">
              {widgets.data?.group.fields.map((field) => (
                <WidgetField
                  key={field.name}
                  field={field}
                  value={settingValue(draft[field.name], field.defaultValue)}
                  onChange={(value) => setDraft((current) => ({ ...current, [field.name]: value }))}
                />
              ))}
            </div>
            <WidgetOverridePanel
              fields={widgets.data?.group.fields ?? []}
              overrides={widgetOverrides(draft.overrides)}
              surface={surface}
              onSurfaceChange={setSurface}
              onChange={(field, value) =>
                setDraft((current) => {
                  const overrides = widgetOverrides(current.overrides);
                  return {
                    ...current,
                    overrides: {
                      ...overrides,
                      [surface]: { ...overrides[surface], [field]: value },
                    },
                  };
                })
              }
              values={draft}
            />
            <div className="wr-widget-grid wr-widget-grid--studio wr-widget-status-grid">
              {widgets.data?.widgets.map((widget) => (
                <article key={widget.name} className="wr-widget-card">
                  <div>
                    <h3>{widget.name}</h3>
                    <StatusBadge tone={widget.enabled ? "success" : "warning"}>{widget.status}</StatusBadge>
                  </div>
                  <p>Uses async app proxy data with empty, loading, mobile, and error states.</p>
                </article>
              ))}
            </div>
          </div>
          <aside className="wr-preview wr-preview--sticky">
            <h2>Live preview</h2>
            <div
              className={`wr-storefront-preview wr-storefront-preview--${String(draft.cardStyle ?? "editorial")}`}
              data-wr-mobile-layout={String(draft.mobileLayout ?? "stacked")}
              style={{
                "--wr-widget-accent": String(draft.accent ?? "#2563eb"),
                "--wr-radius": `${Number(draft.radius ?? 8)}px`,
              } as CSSProperties}
            >
              <span aria-label={`${widgets.data?.preview.averageRating.toFixed(1)} out of 5 stars`}>
                ★★★★★ {widgets.data?.preview.averageRating.toFixed(1)} from {widgets.data?.preview.reviewCount} verified orders
              </span>
              <strong>{widgets.data?.preview.productTitle}</strong>
              <p>"{widgets.data?.preview.quote}"</p>
              <small>{widgets.data?.preview.reviewer}</small>
              <small>{draft.mediaFirst === false ? "Editorial review" : "Media-first proof"} · {draft.inheritTheme === false ? "WhatStar type" : "Theme type"}</small>
              <button type="button">{String(draft.ctaText ?? widgets.data?.preview.ctaText ?? "Share your review")}</button>
            </div>
          </aside>
        </section>
      </AdminState>
    </div>
  );
}

function widgetStatus(widgets: WidgetsResponse["widgets"] | undefined, title: string) {
  if (!widgets?.length) return undefined;
  const normalizedTitle = normalizeWidgetName(title);
  return (
    widgets.find((widget) => normalizeWidgetName(widget.name) === normalizedTitle) ??
    widgets.find((widget) => normalizedTitle.includes(normalizeWidgetName(widget.name)) || normalizeWidgetName(widget.name).includes(normalizedTitle))
  );
}

function normalizeWidgetName(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function ThemeSetupPanel({ links }: { links?: WidgetsResponse["themeSetup"] }) {
  if (!links) return null;
  return (
    <div className="wr-panel wr-theme-installer" data-theme-installer="whatstar">
      <div>
        <h2>Theme editor installer</h2>
        <p>Add the proof surfaces to your theme in a few minutes. Shopify does not report activation back to WhatStar, so confirm each step in the editor and storefront.</p>
      </div>
      <ol className="wr-theme-installer__steps">
        <li><strong>Activate the app embed</strong><span>Loads the async runtime and floating proof entry point.</span></li>
        <li><strong>Add the product reviews block</strong><span>Place it below the product details on the product template.</span></li>
        <li><strong>Add optional proof surfaces</strong><span>Rating badge near the title and media gallery near product media.</span></li>
      </ol>
      <div className="wr-theme-installer__actions">
        <a className="wr-button" href={links.activateEmbed} target="_top" rel="noreferrer">
          Activate app embed
        </a>
        <a className="wr-button wr-button--secondary" href={links.addProductReviews} target="_top" rel="noreferrer">
          Add reviews section
        </a>
        <a className="wr-button wr-button--secondary" href={links.addRatingBadge} target="_top" rel="noreferrer">
          Add rating badge
        </a>
        <a className="wr-button wr-button--secondary" href={links.addMediaGallery} target="_top" rel="noreferrer">
          Add media gallery
        </a>
      </div>
      <small className="wr-theme-installer__note">Live activation status: verify in Shopify’s theme editor. “Ready to add” means the extension is available, not that the live theme is active.</small>
    </div>
  );
}

function WidgetField({
  field,
  onChange,
  value,
}: {
  field: SettingsField;
  onChange: (value: boolean | number | string) => void;
  value: boolean | number | string;
}) {
  return (
    <label className="wr-widget-field">
      <span>{field.label}</span>
      {renderWidgetControl(field, value, onChange)}
      <small>{field.help}</small>
      <em>{field.type}</em>
    </label>
  );
}

function WidgetOverridePanel({
  fields,
  onChange,
  onSurfaceChange,
  overrides,
  surface,
  values,
}: {
  fields: SettingsField[];
  onChange: (field: string, value: SettingValue) => void;
  onSurfaceChange: (surface: string) => void;
  overrides: WidgetOverrides;
  surface: string;
  values: WidgetDraft;
}) {
  const overrideFields = fields.filter((field) => ["mediaFirst", "cardStyle", "mobileLayout", "lightbox", "language"].includes(field.name));
  const current = overrides[surface] ?? {};
  return (
    <details className="wr-panel" id="whatstar-widget-overrides">
      <summary>Per-widget overrides</summary>
      <p>Keep the shared defaults above, then change only what this storefront surface needs.</p>
      <label className="wr-widget-field">
        <span>Storefront surface</span>
        <select value={surface} onChange={(event) => onSurfaceChange(event.target.value)}>
          {widgetCatalog.map((item) => (
            <option key={item.surface} value={item.surface}>
              {item.title}
            </option>
          ))}
        </select>
      </label>
      <fieldset className="wr-widget-form-grid">
        <legend>Override only when needed</legend>
        {overrideFields.map((field) => (
          <WidgetField
            key={`${surface}-${field.name}`}
            field={field}
            value={settingValue(current[field.name] ?? values[field.name], field.defaultValue)}
            onChange={(value) => onChange(field.name, value)}
          />
        ))}
      </fieldset>
    </details>
  );
}

function widgetOverrides(value: unknown): WidgetOverrides {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as WidgetOverrides) : {};
}

function settingValue(value: unknown, fallback: SettingValue): SettingValue {
  return typeof value === "boolean" || typeof value === "number" || typeof value === "string" ? value : fallback;
}

function renderWidgetControl(
  field: SettingsField,
  value: boolean | number | string,
  onChange: (value: boolean | number | string) => void,
) {
  if (field.type === "boolean") {
    return (
      <label className="wr-switch">
        <input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />
        <span>{Boolean(value) ? "Enabled" : "Disabled"}</span>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <select value={String(value)} onChange={(event) => onChange(event.target.value)}>
        {field.options?.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={field.type === "number" ? "number" : field.type}
      value={String(value)}
      onChange={(event) => onChange(field.type === "number" ? Number(event.target.value) : event.target.value)}
    />
  );
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}
